/**
 * Capture OAuth callback params (code, state) from the URL at app bootstrap,
 * before Keycloak runs. Keycloak's check-sso will redirect and replace the URL
 * with its own code/state, so we must read and store the third-party OAuth
 * params on first load.
 *
 * We run this module as the first import in main.tsx so it executes before
 * Keycloak is initialized.
 */
import { getOAuthParamFromLocation } from '@/utils/oauth-params';

/**
 * Detect if a code parameter looks like a Keycloak authorization code.
 * Keycloak codes follow the format: {uuid}.{session_id}.{client_id}
 * where all three parts are UUIDs (e.g., "cba26058-f209-4c37-ab5e-e3c3e082137b.df4d594c-5d63-41ef-9bac-eef9272ccc92.b77fc4a3-1ea4-4fb2-a454-36d0c65f18cf")
 */
function isKeycloakCodeFormat(code: string): boolean {
  if (!code) return false;

  const parts = code.split('.');
  if (parts.length !== 3) return false;

  // UUID pattern: 8-4-4-4-12 hex characters with dashes
  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // All three parts must be valid UUIDs
  return parts.every(part => uuidPattern.test(part));
}

function captureOAuthParamsOnBootstrap(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const code = getOAuthParamFromLocation('code');
  const state = getOAuthParamFromLocation('state');
  const issuer = getOAuthParamFromLocation('iss');
  const error = getOAuthParamFromLocation('error');
  const errorDescription = getOAuthParamFromLocation('error_description');

  // Path must be /dashboard/business-stack/:appKey (OAuth callback route)
  const pathMatch = url.pathname.match(/\/business-stack\/([^/?]+)/);
  const appKey = pathMatch ? pathMatch[1] : null;

  if (!appKey) return;

  const storageKey = `oauth_callback_params_${appKey}`;

  // Check if we already have stored params (don't overwrite)
  try {
    const existingParams = sessionStorage.getItem(storageKey);
    if (existingParams) return;
  } catch {
    // Continue if sessionStorage check fails
  }

  // Capture OAuth errors first - these should be stored and surfaced to the user
  // OAuth errors don't have a code, so we need to capture them separately
  if (error) {
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          error,
          errorDescription: errorDescription || null,
          state: state || null,
          capturedAt: Date.now(),
        })
      );
    } catch {
      // console.warn('Failed to store OAuth error params in sessionStorage');
    }
    return;
  }

  /* Keycloak callback detection:
   * Keycloak includes `iss` pointing to a realm (e.g., /realms/agentic-identity-service)
   */
  const isLikelyKeycloakCallback = Boolean(
    issuer?.includes('/realms/') || isKeycloakCodeFormat(code || '')
  );

  // Store third-party OAuth params as early as possible.
  // `state` is optional for storage; downstream processing handles missing state.
  if (code && !isLikelyKeycloakCallback) {
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ code, state: state || null, capturedAt: Date.now() })
      );
    } catch {
      // console.warn('Failed to store OAuth params in sessionStorage');
    }
  }
}

captureOAuthParamsOnBootstrap();
